/**
 * HeaderSection - Botões de ação principais do PendingDomainsPanel
 */
import React, { memo } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    IconButton,
    InputAdornment
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    Refresh as RefreshIcon,
    GetApp as GetAppIcon,
    Publish as PublishIcon,
    Pending as PendingIcon,
    PlaylistAdd as PlaylistAddIcon,
    AutoAwesome as AutoAwesomeIcon,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';

const HeaderSection = memo(({
    onExport,
    onImport,
    onBulkImportClick,
    onSync,
    onRefresh,
    syncLoading,
    onEnrichBatch,
    selectedCount,
    enrichLoading,
    searchInput = '',
    onSearchChange,
    selectedView,
    views = []
}) => {
    const theme = useTheme();
    const selectedViewData = views.find((view) => view.id === selectedView);

    const actionButtonSx = {
        minHeight: 34,
        borderRadius: 1.5,
        textTransform: 'none',
        fontWeight: 700,
        px: 1.35,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 18px rgba(0, 0, 0, 0.28)'
            : '0 8px 18px rgba(15, 23, 42, 0.10)'
    };

    return (
        <>
        <Box
            aria-hidden="true"
            sx={{
                height: { xs: 430, sm: 324, md: 298, xl: 250 },
                mb: 2.5
            }}
        />
        <Box
            sx={{
                position: 'fixed',
                top: 'calc(var(--admin-appbar-height, 64px) + 12px)',
                left: { xs: 12, md: 'calc(var(--admin-sidebar-width, 240px) + 24px)' },
                right: { xs: 12, md: 24 },
                zIndex: theme.zIndex.appBar + 1,
                p: { xs: 1.25, md: 1.5 },
                boxSizing: 'border-box',
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    md: 'minmax(250px, 0.9fr) minmax(320px, 1.1fr)',
                    xl: 'minmax(280px, 0.8fr) minmax(360px, 540px) minmax(620px, 1fr)'
                },
                gridTemplateAreas: {
                    xs: '"title" "search" "actions"',
                    md: '"title search" "actions actions"',
                    xl: '"title search actions"'
                },
                gap: { xs: 1.1, md: 1.25, xl: 1.5 },
                alignItems: 'center',
                background: theme.palette.mode === 'dark'
                    ? theme.palette.background.paper
                    : theme.palette.background.paper,
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.08)
                    : alpha(theme.palette.common.black, 0.08),
                borderRadius: 2,
                boxShadow: theme.palette.mode === 'dark'
                    ? '0 14px 34px rgba(0, 0, 0, 0.30)'
                    : '0 14px 34px rgba(15, 23, 42, 0.10)',
                '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.black, 0.18)
                        : alpha(theme.palette.common.white, 0.82)
                }
            }}
        >
            <Box sx={{ gridArea: 'title', display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    background: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.06)
                        : alpha(theme.palette.common.black, 0.05),
                    color: 'primary.main',
                    mr: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0
                }}>
                    <PendingIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0, lineHeight: 1.15 }} noWrap>
                        Fila Pendente
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 600, mt: 0.35 }}
                        noWrap
                    >
                        Gerenciamento de domínios aguardando validação HypeStat/Manual
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ gridArea: 'search', minWidth: 0 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={selectedViewData
                        ? `Buscar em ${selectedViewData.name}...`
                        : 'Buscar domínio ou site...'}
                    value={searchInput}
                    onChange={(event) => onSearchChange?.(event.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchInput ? (
                            <IconButton size="small" onClick={() => onSearchChange?.('')} edge="end">
                                <ClearIcon sx={{ fontSize: '1.05rem' }} />
                            </IconButton>
                        ) : null
                    }}
                />
            </Box>

            <Box
                sx={{
                    gridArea: 'actions',
                    display: 'flex',
                    gap: 0.85,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    justifyContent: { xs: 'flex-start', xl: 'flex-end' },
                    minWidth: 0,
                    overflowX: { sm: 'auto' },
                    pb: { sm: 0.25, xl: 0 },
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': {
                        borderRadius: 999,
                        bgcolor: alpha(theme.palette.text.primary, 0.18)
                    }
                }}
            >
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={onRefresh}
                    sx={{ ...actionButtonSx, boxShadow: 'none' }}
                >
                    Atualizar
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    startIcon={<GetAppIcon />}
                    onClick={onExport}
                    disabled={syncLoading}
                    sx={actionButtonSx}
                >
                    Exportar Base
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<PublishIcon />}
                    onClick={onImport}
                    disabled={syncLoading}
                    sx={actionButtonSx}
                >
                    Importar Base
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    color="info"
                    startIcon={<PlaylistAddIcon />}
                    onClick={onBulkImportClick}
                    disabled={syncLoading}
                    sx={actionButtonSx}
                >
                    Lote
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={onEnrichBatch}
                    disabled={selectedCount === 0 || enrichLoading}
                    sx={actionButtonSx}
                >
                    {enrichLoading
                        ? 'Processando...'
                        : `IA${selectedCount > 0 ? ` (${selectedCount})` : ''}`
                    }
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={onSync}
                    disabled={syncLoading}
                    sx={actionButtonSx}
                >
                    {syncLoading ? 'Sinc...' : 'Sincronizar'}
                </Button>
            </Box>
        </Box>
        </>
    );
});

HeaderSection.displayName = 'HeaderSection';

export default HeaderSection;
